import Image from 'next/image';
import React from 'react';

import type { SafetyCasePhotoView } from '../useSafetyPageData';

const PHOTO_SIZES =
  '(min-width: 1540px) 467px, (min-width: 992px) calc((100vw - 120px) / 3), calc(100vw - 40px)';

type Props = {
  customer: string;
  logo: string;
  title: string;
  points: ReadonlyArray<string>;
  photos: ReadonlyArray<SafetyCasePhotoView>;
};

/**
 * ### SafetyCaseCard
 *
 * One deployment as the same glass card the solution pages use for their
 * cases: photos across the top, then the customer's mark, the title, and
 * where the microphones went. Several photos share the top as a strip, one
 * photo fills it.
 *
 * @param props Customer, copy, and photos of one deployment
 * @returns The rendered card element
 */
export default function SafetyCaseCard({
  customer,
  logo,
  title,
  points,
  photos,
}: Props) {
  return (
    <div className="h-full overflow-hidden rounded-[20px] bg-[rgba(255,255,255,0.04)] shadow-[0px_8px_40px_0px_rgba(0,0,0,0.2)] backdrop-blur-[30px]">
      <ul
        className="grid h-[163px] gap-[2px] lg:h-[clamp(163px,17.143vw,240px)]"
        style={{ gridTemplateColumns: `repeat(${photos.length}, 1fr)` }}
      >
        {photos.map((photo) => (
          <li key={photo.src} className="relative">
            <Image
              src={photo.src}
              width={photo.width}
              height={photo.height}
              alt={`${customer} ${photo.caption}`}
              sizes={PHOTO_SIZES}
              quality={86}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute bottom-[8px] left-[8px] rounded-full bg-[rgba(0,0,0,0.55)] px-[8px] py-[3px] text-[12px] font-medium leading-[140%] text-apps-coreGrayWhite">
              {photo.caption}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-[16px] p-[24px] lg:gap-[clamp(16px,2vw,24px)] lg:p-[clamp(24px,2.571vw,36px)]">
        {/*
          The marks arrive as transparent files drawn for light paper, so on the
          dark card they read as smudges. Each sits on the same white tile at the
          same height, so the three cards line up and every mark keeps the
          contrast it was designed for.
        */}
        <div className="flex items-center">
          <span className="inline-flex h-[52px] items-center rounded-[12px] bg-apps-coreGrayWhite px-[16px] lg:h-[clamp(52px,4.5vw,60px)]">
            <Image
              src={logo}
              alt=""
              width={160}
              height={40}
              sizes="160px"
              className="h-[28px] w-auto object-contain lg:h-[clamp(28px,2.4vw,32px)]"
            />
          </span>
          <span className="sr-only">{customer}</span>
        </div>
        <div className="flex flex-col gap-[8px] lg:gap-[clamp(8px,1.333vw,16px)]">
          <h3 className="text-[20px] font-bold leading-[140%] tracking-[-0.4px] text-apps-coreGrayWhite lg:text-[clamp(20px,2vw,28px)] lg:tracking-[-0.56px]">
            {title}
          </h3>
          <ul className="flex flex-col gap-[6px]">
            {points.map((point) => (
              <li
                key={point}
                className="flex items-start gap-[8px] text-[16px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray700 opacity-80 lg:text-[clamp(16px,1.5vw,18px)] lg:leading-[160%] lg:tracking-[-0.36px]"
              >
                <span
                  className="mt-[8px] h-[5px] w-[5px] shrink-0 rounded-full bg-apps-brandGray600 lg:mt-[11px]"
                  aria-hidden="true"
                />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
